import { z } from 'zod';
import type {
  AppSettings,
  BackupData,
  BackupSchema,
  CalibrationMeasurement,
  Container,
  ConversionConfiguration,
  DosageTable,
  MeasuringCup,
  Product,
  RecommendationRule,
  RecoverySnapshot,
  SecurityConfig,
  StainGuide,
  TemperatureGuidance,
  WaterHardness,
} from './types';

const idSchema = z.string().trim().min(1).max(120);
const localizedTextSchema = z.object({
  de: z.string().trim().min(1).max(2000),
  ar: z.string().trim().max(2000).optional(),
});
const isoDateSchema = z.string().datetime({ offset: true });
const nonNegativeFiniteSchema = z.number().finite().min(0);
const positiveFiniteSchema = z.number().finite().positive();
const soilLevelIdSchema = z.enum(['light', 'normal', 'strong', 'veryStrong']);
const textileCategoryIdSchema = z.enum([
  'whiteRobust',
  'colored',
  'delicate',
  'wool',
  'synthetic',
  'cottonLinen',
  'unsure',
]);
const textileColorSchema = z.enum(['white', 'colored', 'delicate', 'unsure']);

export const appSettingsSchema: z.ZodType<AppSettings> = z.object({
  id: z.literal('app'),
  appName: z.string().trim().min(1).max(100),
  schoolName: z.string().trim().max(200),
  language: z.enum(['de', 'ar']),
  theme: z.enum(['system', 'light', 'dark']),
  defaultWaterHardnessId: idSchema,
  waterHardnessLocked: z.boolean(),
  welcomeDismissed: z.boolean(),
  autoLockMinutes: z.number().int().min(1).max(240),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
});

export const productSchema: z.ZodType<Product> = z.object({
  id: idSchema,
  name: localizedTextSchema,
  category: localizedTextSchema,
  active: z.boolean(),
  supportedSoilLevels: z.array(soilLevelIdSchema).min(1).max(4),
  instructions: z.array(localizedTextSchema).max(30),
  safetyNotes: z.array(localizedTextSchema).max(30),
  sortOrder: z.number().int().min(0),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
});

export const dosageTableSchema: z.ZodType<DosageTable> = z.object({
  id: idSchema,
  productId: idSchema,
  waterHardnessId: idSchema,
  soilLevelId: soilLevelIdSchema,
  mlPerKg: positiveFiniteSchema.max(1000),
});

export const waterHardnessSchema: z.ZodType<WaterHardness> = z
  .object({
    id: idSchema,
    name: localizedTextSchema,
    description: localizedTextSchema,
    minDh: nonNegativeFiniteSchema.nullable(),
    maxDh: nonNegativeFiniteSchema.nullable(),
    active: z.boolean(),
    sortOrder: z.number().int().min(0),
  })
  .refine((value) => value.minDh === null || value.maxDh === null || value.minDh <= value.maxDh, {
    message: 'minDh must not exceed maxDh',
    path: ['minDh'],
  });

export const containerSchema: z.ZodType<Container> = z.object({
  id: idSchema,
  name: localizedTextSchema,
  tareWeightG: nonNegativeFiniteSchema.max(100_000),
  active: z.boolean(),
  isDefault: z.boolean(),
  sortOrder: z.number().int().min(0),
});

export const measuringCupSchema: z.ZodType<MeasuringCup> = z.object({
  id: idSchema,
  name: localizedTextSchema,
  capacityMl: positiveFiniteSchema.max(100_000),
  emptyWeightG: nonNegativeFiniteSchema.max(100_000),
  active: z.boolean(),
  sortOrder: z.number().int().min(0),
});

export const conversionConfigurationSchema: z.ZodType<ConversionConfiguration> = z
  .object({
    productId: idSchema,
    schoolGramsPerMl: z.literal(1),
    calibratedGramsPerMl: positiveFiniteSchema.max(20).nullable(),
    activeConversionMode: z.enum(['school', 'calibrated']),
    calibrationStatus: z.enum(['notCalibrated', 'calibrated', 'manual']),
    calibrationDate: isoDateSchema.nullable(),
    calibrationNote: z.string().trim().max(2000),
    updatedAt: isoDateSchema,
  })
  .superRefine((value, context) => {
    if (value.activeConversionMode === 'calibrated' && value.calibratedGramsPerMl === null) {
      context.addIssue({
        code: 'custom',
        message: 'Calibrated mode requires a calibrated value.',
        path: ['calibratedGramsPerMl'],
      });
    }
    if (value.calibrationStatus === 'notCalibrated' && value.calibratedGramsPerMl !== null) {
      context.addIssue({
        code: 'custom',
        message: 'An uncalibrated configuration cannot contain a calibrated value.',
        path: ['calibratedGramsPerMl'],
      });
    }
    if (value.calibrationStatus === 'manual' && value.calibrationNote.length === 0) {
      context.addIssue({
        code: 'custom',
        message: 'A manual calibration requires a note.',
        path: ['calibrationNote'],
      });
    }
  });

export const calibrationMeasurementSchema: z.ZodType<CalibrationMeasurement> = z
  .object({
    id: idSchema,
    runId: idSchema,
    productId: idSchema,
    cupId: idSchema.nullable(),
    cupNameSnapshot: z.string().trim().max(200),
    measuredMilliliters: positiveFiniteSchema.max(100_000),
    measuredGrams: positiveFiniteSchema.max(100_000),
    gramsPerMl: positiveFiniteSchema.max(20),
    measuredAt: isoDateSchema,
  })
  .refine(
    (value) => Math.abs(value.gramsPerMl - value.measuredGrams / value.measuredMilliliters) < 1e-9,
    { message: 'gramsPerMl does not match the measurement ratio', path: ['gramsPerMl'] },
  );

export const temperatureGuidanceSchema: z.ZodType<TemperatureGuidance> = z.object({
  id: idSchema,
  productId: idSchema,
  textileCategoryIds: z.array(textileCategoryIdSchema).min(1).max(7),
  guidance: localizedTextSchema,
  active: z.boolean(),
  sortOrder: z.number().int().min(0),
});

export const recommendationRuleSchema: z.ZodType<RecommendationRule> = z.object({
  id: idSchema,
  textileCategoryId: textileCategoryIdSchema,
  colorScope: z.union([textileColorSchema, z.literal('any')]),
  productId: idSchema.nullable(),
  note: localizedTextSchema,
  active: z.boolean(),
  priority: z.number().int().min(0).max(10_000),
});

export const stainGuideSchema: z.ZodType<StainGuide> = z.object({
  id: idSchema,
  title: localizedTextSchema,
  shortDescription: localizedTextSchema,
  allowedTextileCategoryIds: z.array(textileCategoryIdSchema).min(1).max(7),
  allowedColors: z.array(textileColorSchema).min(1).max(4),
  steps: z.array(localizedTextSchema).min(1).max(30),
  warnings: z.array(localizedTextSchema).max(30),
  recommendedWaterTemperature: localizedTextSchema.nullable(),
  prohibitedActions: z.array(localizedTextSchema).max(30),
  recommendedProductId: idSchema.nullable(),
  active: z.boolean(),
  sortOrder: z.number().int().min(0),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
});

export const securityConfigSchema: z.ZodType<SecurityConfig> = z.object({
  id: z.literal('teacher-pin'),
  algorithm: z.literal('PBKDF2-SHA-256'),
  algorithmVersion: z.literal(1),
  iterations: z.number().int().min(100_000).max(2_000_000),
  salt: z.string().min(16).max(200),
  derivedHash: z.string().min(32).max(500),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
});

export const backupDataSchema: z.ZodType<BackupData> = z.object({
  settings: z.array(appSettingsSchema).length(1),
  products: z.array(productSchema).max(500),
  dosageTables: z.array(dosageTableSchema).max(20_000),
  waterHardness: z.array(waterHardnessSchema).max(100),
  containers: z.array(containerSchema).max(500),
  measuringCups: z.array(measuringCupSchema).max(500),
  conversionConfigurations: z.array(conversionConfigurationSchema).max(500),
  calibrationMeasurements: z.array(calibrationMeasurementSchema).max(20_000),
  temperatureGuidance: z.array(temperatureGuidanceSchema).max(10_000),
  recommendationRules: z.array(recommendationRuleSchema).max(10_000),
  stainGuides: z.array(stainGuideSchema).max(5_000),
});

export const backupSchema: z.ZodType<BackupSchema> = z.object({
  format: z.literal('waschpilot-school-backup'),
  backupSchemaVersion: z.literal(1),
  databaseSchemaVersion: z.number().int().positive(),
  appVersion: z.string().trim().min(1).max(100),
  exportedAt: isoDateSchema,
  data: backupDataSchema,
});

export const recoverySnapshotSchema: z.ZodType<RecoverySnapshot> = z.object({
  id: z.number().int().positive().optional(),
  createdAt: isoDateSchema,
  reason: z.literal('preImport'),
  payload: backupSchema,
});

export type BackupRelationIssue = {
  path: string;
  code:
    | 'duplicateId'
    | 'duplicateDosageCombination'
    | 'multipleDefaultContainers'
    | 'missingProduct'
    | 'missingWaterHardness'
    | 'unsupportedSoil'
    | 'missingCup'
    | 'missingDefaultWaterHardness';
};

function duplicateIssues<T>(
  values: readonly T[],
  id: (value: T) => string,
  path: string,
): BackupRelationIssue[] {
  const seen = new Set<string>();
  return values.flatMap((value) => {
    const key = id(value);
    if (seen.has(key)) return [{ path: `${path}.${key}`, code: 'duplicateId' as const }];
    seen.add(key);
    return [];
  });
}

export function validateBackupRelations(data: BackupData): BackupRelationIssue[] {
  const productIds = new Set(data.products.map(({ id }) => id));
  const waterIds = new Set(data.waterHardness.map(({ id }) => id));
  const cupIds = new Set(data.measuringCups.map(({ id }) => id));
  const supportedSoil = new Map(
    data.products.map((product) => [product.id, new Set(product.supportedSoilLevels)]),
  );
  const issues: BackupRelationIssue[] = [
    ...duplicateIssues(data.products, ({ id }) => id, 'products'),
    ...duplicateIssues(data.dosageTables, ({ id }) => id, 'dosageTables'),
    ...duplicateIssues(data.waterHardness, ({ id }) => id, 'waterHardness'),
    ...duplicateIssues(data.containers, ({ id }) => id, 'containers'),
    ...duplicateIssues(data.measuringCups, ({ id }) => id, 'measuringCups'),
    ...duplicateIssues(
      data.conversionConfigurations,
      ({ productId }) => productId,
      'conversionConfigurations',
    ),
    ...duplicateIssues(data.calibrationMeasurements, ({ id }) => id, 'calibrationMeasurements'),
    ...duplicateIssues(data.temperatureGuidance, ({ id }) => id, 'temperatureGuidance'),
    ...duplicateIssues(data.recommendationRules, ({ id }) => id, 'recommendationRules'),
    ...duplicateIssues(data.stainGuides, ({ id }) => id, 'stainGuides'),
  ];

  const dosageCombinations = new Set<string>();
  data.dosageTables.forEach((entry, index) => {
    const key = `${entry.productId}\u0000${entry.waterHardnessId}\u0000${entry.soilLevelId}`;
    if (dosageCombinations.has(key)) {
      issues.push({ path: `dosageTables.${index}`, code: 'duplicateDosageCombination' });
    }
    dosageCombinations.add(key);
  });

  const defaultContainers = data.containers
    .map((container, index) => ({ container, index }))
    .filter(({ container }) => container.isDefault);
  for (const { index } of defaultContainers.slice(1)) {
    issues.push({ path: `containers.${index}.isDefault`, code: 'multipleDefaultContainers' });
  }

  if (!waterIds.has(data.settings[0]?.defaultWaterHardnessId ?? '')) {
    issues.push({ path: 'settings.0.defaultWaterHardnessId', code: 'missingDefaultWaterHardness' });
  }

  data.dosageTables.forEach((entry, index) => {
    if (!productIds.has(entry.productId)) {
      issues.push({ path: `dosageTables.${index}.productId`, code: 'missingProduct' });
    }
    if (!waterIds.has(entry.waterHardnessId)) {
      issues.push({ path: `dosageTables.${index}.waterHardnessId`, code: 'missingWaterHardness' });
    }
    if (!supportedSoil.get(entry.productId)?.has(entry.soilLevelId)) {
      issues.push({ path: `dosageTables.${index}.soilLevelId`, code: 'unsupportedSoil' });
    }
  });

  data.conversionConfigurations.forEach((configuration, index) => {
    if (!productIds.has(configuration.productId)) {
      issues.push({ path: `conversionConfigurations.${index}.productId`, code: 'missingProduct' });
    }
  });
  data.calibrationMeasurements.forEach((measurement, index) => {
    if (!productIds.has(measurement.productId)) {
      issues.push({ path: `calibrationMeasurements.${index}.productId`, code: 'missingProduct' });
    }
    if (measurement.cupId !== null && !cupIds.has(measurement.cupId)) {
      issues.push({ path: `calibrationMeasurements.${index}.cupId`, code: 'missingCup' });
    }
  });
  data.temperatureGuidance.forEach((guidance, index) => {
    if (!productIds.has(guidance.productId)) {
      issues.push({ path: `temperatureGuidance.${index}.productId`, code: 'missingProduct' });
    }
  });
  data.recommendationRules.forEach((rule, index) => {
    if (rule.productId !== null && !productIds.has(rule.productId)) {
      issues.push({ path: `recommendationRules.${index}.productId`, code: 'missingProduct' });
    }
  });
  data.stainGuides.forEach((guide, index) => {
    if (guide.recommendedProductId !== null && !productIds.has(guide.recommendedProductId)) {
      issues.push({ path: `stainGuides.${index}.recommendedProductId`, code: 'missingProduct' });
    }
  });

  return issues;
}

export function hasMissingArabic(value: { de: string; ar?: string }): boolean {
  return value.ar?.trim().length === 0 || value.ar === undefined;
}

export function localizedText(value: { de: string; ar?: string }, locale: 'de' | 'ar') {
  if (locale === 'ar' && value.ar?.trim()) {
    return { text: value.ar, usedFallback: false } as const;
  }
  return { text: value.de, usedFallback: locale === 'ar' } as const;
}
